import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useState } from "react";
import { ExpandableTable, Cells } from "@ploy-ui/core";
import { ApplicationInfoResource, LoginResource, IntegrationClientResource } from "@ploy-lib/rest-resources";
import { useResource } from "@rest-hooks/core";
import { TablePagination, makeStyles } from "@material-ui/core";
import clsx from "clsx";
import { SalespersonSelector } from "./SalespersonSelector";
import { IntegrationSelector } from "./IntegrationSelector";
import { VendorSalespersonSelector } from "./VendorSalespersonSelector";
import { DatePicker } from "@material-ui/pickers";
const useStyles = makeStyles(theme => ({
    root: {
        width: "100%",
        flexGrow: 1,
        padding: theme.spacing(2),
        [theme.breakpoints.up("lg")]: {
            padding: theme.spacing(4)
        }
    },
    applicationStatusNameExternal: {
        width: 160,
        textAlign: "center"
    },
    applicationNumber: {
        width: 100
    },
    customerNameInner: {
        display: "flex",
        alignItems: "center",
        "& > div": {
            marginRight: 10
        }
    },
    customerNameText: {
        fontWeight: "bold"
    },
    datePickerInput: {
        fontWeight: "bold",
        fontSize: "0.875rem"
    }
}), { name: "ApplicationList" });
const fromDataToSalespersonSelector = (props) => {
    return (_jsx(SalespersonSelector, { salespersonId: props.rowData?.salesPersonId, applicationId: props.rowData?.applicationId }));
};
const fromDataToVendorSalespersonSelector = (props) => {
    return (_jsx(VendorSalespersonSelector, { salespersonId: props.rowData?.salesPersonId, vendorId: props.rowData?.vendorId, applicationId: props.rowData?.applicationId }));
};
const getColumnData = vendorSalespeople => [
    {
        id: "applicationStatusNameExternal",
        label: "Status",
        component: props => (_jsx(Cells.ColoredStatus, { statusKey: "applicationStatusId", ...props })),
        hidden: { xsDown: true },
        hiddenInExpandable: { smUp: true }
    },
    {
        id: "applicationNumber",
        label: "Saksnr.",
        hidden: { smDown: true }
    },
    {
        id: "customerName",
        label: "Kunde",
        component: props => (_jsx(Cells.DoubleLineSmDown, { statusKey: "applicationStatusId", secondLineKey: "applicationNumber", ...props }))
    },
    {
        id: "productName",
        label: "Produkt",
        hidden: { smDown: true },
        hiddenInExpandable: { mdUp: true }
    },
    vendorSalespeople ? { id: "customerNumber", label: "Kundenummer" } : {},
    vendorSalespeople ? { id: "vendorName", label: "Salgskanal" } : {},
    !vendorSalespeople
        ? {
            id: "documentStatusNameExternal",
            label: "Dokumentstatus"
        }
        : {},
    {
        id: "salespersonName",
        label: "Selger",
        component: vendorSalespeople
            ? fromDataToVendorSalespersonSelector
            : fromDataToSalespersonSelector
    }
];
export const SalespersonAssignment = ({ className }) => {
    const classes = useStyles();
    const { vendor: currentVendor } = useResource(LoginResource.status(), {});
    const integrations = (useResource(IntegrationClientResource.list(), {
        vendorId: currentVendor?.id
    }) || []);
    const [integration, setIntegration] = useState(integrations[0]);
    const today = () => {
        const currentDate = new Date();
        currentDate.setHours(23, 59, 0, 0);
        return currentDate;
    };
    const threeMonthsAgo = () => {
        const backInTheDay = new Date();
        backInTheDay.setHours(23, 59, 0, 0);
        backInTheDay.setDate(backInTheDay.getDate() - 91);
        return backInTheDay;
    };
    const [fromDate, setFromDate] = useState(threeMonthsAgo().toISOString());
    const [toDate, setToDate] = useState(today().toISOString());
    const useCarwebView = integrations.some(x => x.code === "carweb");
    const columns = useMemo(() => getColumnData(!useCarwebView).map(c => ({
        ...c,
        className: c.id == null
            ? undefined
            : classes[c.id]
    })), [classes, useCarwebView]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(20);
    const params = useMemo(() => ({
        integrationClientId: useCarwebView
            ? integration?.integrationClientId
            : undefined,
        salepersonId: useCarwebView
            ? integration?.defaultSalespersonId
            : undefined,
        vendorId: currentVendor?.id,
        pageSize: rowsPerPage,
        page: page + 1,
        sortBy: "createdDate.desc",
        useVendorSpecifiedSearch: !useCarwebView,
        fromDate: useCarwebView ? undefined : fromDate,
        toDate: useCarwebView ? undefined : toDate
    }), [
        currentVendor?.id,
        fromDate,
        integration?.defaultSalespersonId,
        integration?.integrationClientId,
        page,
        rowsPerPage,
        toDate,
        useCarwebView
    ]);
    const { records, pageSize, recordCount } = useResource(ApplicationInfoResource.list(), params);
    return (_jsxs("div", { className: clsx(className, classes.root), children: [integrations.length > 1 && (_jsx(IntegrationSelector, { integrationId: integration?.integrationClientId, vendorId: currentVendor?.id, setIntegration: integration => setIntegration(integration) })), !useCarwebView && (_jsxs(_Fragment, { children: [_jsx(DatePicker, { label: "Fra", value: fromDate, format: "dd.MM.yyyy", onChange: date => {
                            var newDate = date?.toJSDate() ?? threeMonthsAgo();
                            newDate.setHours(23, 59, 0, 0);
                            setFromDate(newDate.toISOString());
                        }, inputProps: { className: classes.datePickerInput } }), _jsx(DatePicker, { label: "Til", value: toDate, format: "dd.MM.yyyy", onChange: date => {
                            var newDate = date?.toJSDate() ?? today();
                            newDate.setHours(23, 59, 0, 0);
                            setToDate(newDate.toISOString());
                        }, inputProps: { className: classes.datePickerInput } })] })), _jsxs(_Fragment, { children: [_jsx(ExpandableTable, { idColumn: "applicationId", tableData: records, columnData: columns, expandable: false }), _jsx(TablePagination, { rowsPerPageOptions: [10, 20, 50], component: "div", count: recordCount, rowsPerPage: pageSize, page: page, onPageChange: (_, newPage) => {
                            setPage(newPage);
                        }, onRowsPerPageChange: event => {
                            setRowsPerPage(parseInt(event.target.value, 10));
                            setPage(0);
                        } })] })] }));
};
//# sourceMappingURL=SalespersonAssignment.js.map